import { useState } from 'react'

import { DayAndTime, isToday, TimeOfDay } from './StartDateAndPostFrequencyModal'
import { PostingCadence } from '../../ScheduleContent'
import dayjs, { Dayjs } from 'dayjs'
import { DrawerProps } from 'components/Drawer/Root'
import { PostFrequency } from './components/PostFrequency/PostFrequency'
import { useMachine } from '@xstate/react'
import { startDateAndPostFrequencyMobileMachine } from './startDateAndPostFrequencyMobileMachine'
import { ChooseTimeOfDay } from './components/ChooseTimeOfDay/ChooseTimeOfDay'
import { Home } from './components/Home/Home'
import { useRootStore } from 'stores/RootStore'
import { Drawer } from 'components/Drawer'
import { ChevronLeft } from 'blaze-ui/icons/24'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props {
  initialDays: DayAndTime[]
  initialStartDate: Dayjs
  onScheduleChange: (schedule: { startDate: Dayjs; daysAndTimes: DayAndTime[]; isCustom: boolean }) => void
}

export const StartDateAndPostFrequencyDrawer = (props: DrawerProps & Props) => {
  const { initialStartDate, initialDays, onScheduleChange } = props
  const [startDate, setStartDate] = useState(initialStartDate)
  const [postingCadence, setPostingCadence] = useState<PostingCadence>(PostingCadence.Once)
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>(TimeOfDay.Random)
  const [days, setDays] = useState<DayAndTime[]>(initialDays)
  const [customTime, setCustomTime] = useState<string | undefined>()
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable no-nested-ternary */
  const [customAmOrPm, setCustomAmOrPm] = useState(
    isToday(initialStartDate) ? (dayjs().hour() < 12 ? 'am' : 'pm') : 'am',
  )
  /* eslint-enable no-nested-ternary */
  const [isCreatingCustomSchedule, setIsCreatingCustomSchedule] = useState(false)
  const drawersStore = useRootStore().drawersStore
  const [currentPage, send] = useMachine(startDateAndPostFrequencyMobileMachine())

  const applySchedule = () => {
    onScheduleChange({ startDate, daysAndTimes: days, isCustom: postingCadence === PostingCadence.Custom })
    drawersStore.closeDrawer()
  }

  const isChoosingFrequency = currentPage.matches('chooseFrequency') || currentPage.matches('customSchedule')

  const goBack = () => {
    if (currentPage.matches('home')) {
      drawersStore.closeDrawer()
      return
    }

    if (currentPage.matches('customSchedule')) {
      setIsCreatingCustomSchedule(false)
    }

    send('GO_BACK')
  }

  const headingText = () => {
    if (currentPage.matches('home')) {
      return 'Start date and post frequency'
    }

    if (currentPage.matches('chooseFrequency')) {
      return 'Post frequency'
    }

    if (currentPage.matches('customSchedule')) {
      return 'Custom week days'
    }

    if (currentPage.matches('chooseTimeOfDay')) {
      return 'Time of day'
    }
  }

  return (
    <Drawer.Root {...props} handleClose={goBack}>
      <Drawer.Portal>
        <Drawer.Content fullHeight>
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top>
              <Drawer.Close label="" icon={ChevronLeft} />
              <Drawer.Title asChild>
                <Heading level={5}>{headingText()}</Heading>
              </Drawer.Title>
            </Drawer.Top>
            {currentPage.matches('home') && (
              <Home
                startDate={startDate}
                setStartDate={setStartDate}
                timeOfDay={timeOfDay}
                setTimeOfDay={setTimeOfDay}
                customTime={customTime}
                customAmOrPm={customAmOrPm}
                setCustomAmOrPm={setCustomAmOrPm}
                days={days}
                setDays={setDays}
                postingCadence={postingCadence}
                send={send}
                onConfirm={applySchedule}
              />
            )}
            {isChoosingFrequency && (
              <PostFrequency
                startDate={startDate}
                initialPostingCadence={postingCadence}
                initialTimeOfDay={timeOfDay}
                initialDays={days}
                customTime={customTime}
                send={send}
                onConfirm={(newPostingCadence: PostingCadence, newDays: DayAndTime[]) => {
                  setPostingCadence(newPostingCadence)
                  setDays(newDays)
                }}
                onScheduleChange={onScheduleChange}
                isCreatingCustomSchedule={isCreatingCustomSchedule}
                setIsCreatingCustomSchedule={setIsCreatingCustomSchedule}
              />
            )}
            {currentPage.matches('chooseTimeOfDay') && (
              <ChooseTimeOfDay
                initialDays={days}
                initialTimeOfDay={timeOfDay}
                initialAmOrPm={customAmOrPm}
                initialCustomTime={customTime}
                startDate={startDate}
                onConfirm={(
                  newDays: DayAndTime[],
                  newCustomTime: string | undefined,
                  newCustomAmOrPm: string,
                  newTimeOfDay,
                ) => {
                  setDays(newDays)
                  setTimeOfDay(newTimeOfDay)
                  setCustomTime(newCustomTime)
                  setCustomAmOrPm(newCustomAmOrPm)
                  send('CONFIRM')
                }}
              />
            )}
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
